import { AcademicsForm, hasInvalidAcademics } from './academicsForm';
import { DistrictInformationForm, hasInvalidDistrictInformation } from './districtInformationForm';
import { parseConfigurationLocation } from './eoaConfigurationPaths';
import { EoaaInfoForm, hasInvalidEoaaInfo } from './eoaaInfoForm';
import { EoaaInfo2Form, hasInvalidEoaaInfo2 } from './eoaaInfo2Form';
import { GradingForm, hasInvalidGrading } from './gradingForm';
import { SchoolInfoForm, SchoolInfoRow, hasInvalidSchoolInfo } from './schoolInfoForm';
import { ConfigurationStepId, ConfigurationStepStatus } from './eoaConfigurationSteps';

const hasText = (value: string): boolean => Boolean(value.trim());
const hasPeople = (people: { name: string; email: string }[]): boolean =>
  people.some((person) => hasText(person.name) && hasText(person.email));
const hasAnyPeople = (people: { name: string; email: string }[]): boolean =>
  people.some((person) => hasText(person.name) || hasText(person.email));

const districtStatus = (form: DistrictInformationForm): ConfigurationStepStatus => {
  const started = Boolean(
    form.stateId ||
      hasText(form.districtName) ||
      hasText(form.districtAliases) ||
      hasText(form.sis) ||
      hasText(form.lms) ||
      form.dsaSigned
  );
  if (!started) return 'not-started';
  return form.stateId && hasText(form.districtName) && form.dsaSigned && !hasInvalidDistrictInformation(form)
    ? 'completed'
    : 'in-progress';
};

const academicsStatus = (form: AcademicsForm): ConfigurationStepStatus => {
  const names = form.periodNames.map((period) => period.name.trim()).filter(Boolean);
  const aliasStarted = form.aliasMappings.some(
    (mapping) => mapping.periodName.trim() || mapping.aliases.some((alias) => alias.value.trim())
  );
  const started = Boolean(
    form.termCount ||
      names.length ||
      aliasStarted ||
      hasText(form.crossSchoolEnrollment) ||
      hasText(form.studentTransfers) ||
      hasText(form.dualEnrollment)
  );
  if (!started) return 'not-started';
  const termCount = Number(form.termCount);
  const requiredComplete =
    Number.isInteger(termCount) &&
    termCount > 0 &&
    names.length >= 1 &&
    hasText(form.studentTransfers) &&
    hasText(form.dualEnrollment);
  return requiredComplete && !hasInvalidAcademics(form) ? 'completed' : 'in-progress';
};

const gradingStatus = (form: GradingForm): ConfigurationStepStatus => {
  const gradeMapsStarted = form.gradeMaps.some((row) => hasText(row.letter) || hasText(row.number));
  const started = Boolean(
    form.defaultCredits ||
      form.gradingSystem ||
      form.failingNumber ||
      form.failingLetter ||
      form.carnegieUnits ||
      gradeMapsStarted ||
      hasText(form.multipleCreditSystems)
  );
  if (!started) return 'not-started';
  const thresholdComplete =
    form.gradingSystem === 'NUMERIC'
      ? hasText(form.failingNumber)
      : form.gradingSystem === 'LETTER'
        ? hasText(form.failingLetter)
        : hasText(form.failingNumber) && hasText(form.failingLetter);
  const gradeMapsComplete =
    form.gradeMaps.length > 0 && form.gradeMaps.every((row) => hasText(row.letter) && hasText(row.number));
  return hasText(form.defaultCredits) &&
    Boolean(form.gradingSystem) &&
    thresholdComplete &&
    hasText(form.carnegieUnits) &&
    gradeMapsComplete &&
    !hasInvalidGrading(form)
    ? 'completed'
    : 'in-progress';
};

const eoaaInfo1Status = (form: EoaaInfoForm): ConfigurationStepStatus => {
  const staffRoles = form.staffRoles.some((role) => hasText(role.name));
  const estimates = Boolean(form.estimatedFamilies || form.estimatedStudents || form.estimatedEducators);
  const started = Boolean(
    form.yearEnding ||
      form.workshopDate ||
      form.minCellSize ||
      hasAnyPeople(form.districtDataLeads) ||
      hasAnyPeople(form.leadFacilitators) ||
      hasAnyPeople(form.facilitators) ||
      form.additionalLanguageIds.length ||
      staffRoles ||
      estimates
  );
  if (!started) return 'not-started';
  const complete =
    hasText(form.yearEnding) &&
    hasText(form.workshopDate) &&
    hasText(form.minCellSize) &&
    hasPeople(form.districtDataLeads) &&
    hasPeople(form.leadFacilitators) &&
    hasPeople(form.facilitators) &&
    staffRoles &&
    hasText(form.estimatedFamilies) &&
    hasText(form.estimatedStudents) &&
    hasText(form.estimatedEducators);
  return complete && !hasInvalidEoaaInfo(form) ? 'completed' : 'in-progress';
};

const eoaaInfo2Status = (form: EoaaInfo2Form): ConfigurationStepStatus => {
  const cells = Object.values(form.cells).flatMap((column) => Object.values(column));
  const started = form.iheNames.some(hasText) || cells.some((cell) => hasText(cell.credits) || hasText(cell.note));
  if (!started) return 'not-started';
  const complete = form.iheNames.every(hasText) && cells.every((cell) => hasText(cell.credits));
  return complete && !hasInvalidEoaaInfo2(form) ? 'completed' : 'in-progress';
};

const schoolStatus = (school: SchoolInfoRow): ConfigurationStepStatus => {
  const started = Boolean(
    hasText(school.name) ||
      school.isPrimary ||
      hasText(school.schoolNumber) ||
      hasText(school.schoolType) ||
      school.graduationStatus ||
      school.requiresSat ||
      school.requiresAct ||
      school.hasCteCourses ||
      school.hasDetailedCteClusters ||
      hasText(school.highWageThreshold) ||
      hasText(school.highGrowthThreshold) ||
      hasText(school.regionalAreaType) ||
      hasText(school.regionalAreaTitle)
  );
  if (!started) return 'not-started';

  const baseComplete =
    hasText(school.name) &&
    Boolean(school.isPrimary) &&
    hasText(school.schoolNumber) &&
    hasText(school.schoolType) &&
    Boolean(school.graduationStatus) &&
    Boolean(school.requiresSat) &&
    Boolean(school.requiresAct) &&
    Boolean(school.hasCteCourses) &&
    hasText(school.regionalAreaType) &&
    hasText(school.regionalAreaTitle);
  if (!baseComplete) return 'in-progress';
  if (school.hasCteCourses !== 'yes') return 'completed';
  if (!school.hasDetailedCteClusters) return 'in-progress';
  if (school.hasDetailedCteClusters !== 'yes') return 'completed';
  return hasText(school.highWageThreshold) &&
    hasText(school.highGrowthThreshold) &&
    !hasInvalidSchoolInfo({ schools: [school] })
    ? 'completed'
    : 'in-progress';
};

const schoolInfoStatus = (form: SchoolInfoForm): ConfigurationStepStatus => {
  if (form.schools.length === 0) return 'not-started';
  const complete = form.schools.every((school) => schoolStatus(school) === 'completed');
  return complete && !hasInvalidSchoolInfo(form) ? 'completed' : 'in-progress';
};

const configurationStepStatuses = ({
  districtInformation,
  academics,
  grading,
  eoaaInfo,
  eoaaInfo2,
  schoolInfo,
}: {
  districtInformation: DistrictInformationForm;
  academics: AcademicsForm;
  grading: GradingForm;
  eoaaInfo: EoaaInfoForm;
  eoaaInfo2: EoaaInfo2Form;
  schoolInfo: SchoolInfoForm;
}): Record<ConfigurationStepId, ConfigurationStepStatus> => {
  const statuses: Record<ConfigurationStepId, ConfigurationStepStatus> = {
    'district-information': districtStatus(districtInformation),
    academics: academicsStatus(academics),
    grading: gradingStatus(grading),
    'eoaa-info-1': eoaaInfo1Status(eoaaInfo),
    'eoaa-info-2': eoaaInfo2Status(eoaaInfo2),
    'school-info': schoolInfoStatus(schoolInfo),
    'review-and-submit': 'not-started',
  };
  statuses['review-and-submit'] = Object.entries(statuses)
    .filter(([stepId]) => stepId !== 'review-and-submit')
    .every(([, status]) => status === 'completed')
    ? 'in-progress'
    : 'not-started';
  return statuses;
};

const hasInvalidConfigurationValues = ({
  districtInformation,
  academics,
  grading,
  eoaaInfo,
  eoaaInfo2,
  schoolInfo,
}: {
  districtInformation: DistrictInformationForm;
  academics: AcademicsForm;
  grading: GradingForm;
  eoaaInfo: EoaaInfoForm;
  eoaaInfo2: EoaaInfo2Form;
  schoolInfo: SchoolInfoForm;
}): boolean =>
  hasInvalidDistrictInformation(districtInformation) ||
  hasInvalidAcademics(academics) ||
  hasInvalidGrading(grading) ||
  hasInvalidEoaaInfo(eoaaInfo) ||
  hasInvalidEoaaInfo2(eoaaInfo2) ||
  hasInvalidSchoolInfo(schoolInfo);

const shouldBlockInvalidConfigurationNavigation = (
  currentPathname: string,
  nextPathname: string,
  hasInvalidValues: boolean
): boolean => {
  if (!hasInvalidValues) return false;
  const current = parseConfigurationLocation(currentPathname);
  const next = parseConfigurationLocation(nextPathname);
  if (current && next && current.stepId === next.stepId) return false;
  return true;
};

export {
  configurationStepStatuses,
  hasInvalidConfigurationValues,
  schoolStatus,
  shouldBlockInvalidConfigurationNavigation,
};
